/**
 * Renders ONE reward in human language. Switch is structural — we
 * only interpret the `type` discriminator and the numeric fields.
 * Plan display names come from the backend (the `tierName` on the
 * preview response, OR the `plan` slug for trigger-bound rewards
 * which we leave as-is) — we deliberately do NOT duplicate
 * config/planConfig.js client-side.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Reward } from '../types';

interface RewardSummaryProps {
  reward: Reward;
}

/**
 * Format a number with thousands separators, leaving NaN/Infinity to
 * surface as the literal value (so a bad payload screams in the UI
 * rather than rendering silently as "NaN credits").
 */
const fmt = (n: number) =>
  Number.isFinite(n) ? n.toLocaleString() : String(n);

/** A small helper so we can give 'cadet' → 'Cadet' without a real lookup. */
const titleCasePlan = (p?: string | null) =>
  p ? p.charAt(0).toUpperCase() + p.slice(1) : '';

function renderOne(reward: Reward): React.ReactNode {
  switch (reward.type) {
    case 'free_months': {
      const planName = titleCasePlan(reward.plan);
      const monthsLabel = `${reward.months} month${reward.months === 1 ? '' : 's'}`;
      return planName
        ? `${monthsLabel} of ${planName}, free`
        : `${monthsLabel} of free access`;
    }
    case 'credits_grant':
      return `${fmt(reward.amount)} Loxia credits added to your balance`;
    case 'credits_on_subscribe':
      return `${fmt(reward.amount)} Loxia credits when you start any ${titleCasePlan(reward.trigger.plan)} plan`;
    case 'months_on_subscribe': {
      const monthsLabel = `${reward.months} month${reward.months === 1 ? '' : 's'}`;
      return `${monthsLabel} of ${titleCasePlan(reward.trigger.plan)} free when you start a ${titleCasePlan(reward.trigger.plan)} plan`;
    }
    case 'combo':
      // Combos are rendered as a bullet list by the caller — we shouldn't
      // hit this branch from <RewardSummary> directly, but if we do
      // we degrade gracefully to a comma-joined fallback.
      return reward.rewards
        .map((r) => renderOne(r))
        .filter(Boolean)
        .join(' + ');
    default:
      // Unknown type — render the raw JSON discriminator so a backend
      // schema change is at least debuggable from the UI.
      return `Unknown reward (${(reward as { type: string }).type})`;
  }
}

const RewardSummary: React.FC<RewardSummaryProps> = ({ reward }) => {
  if (reward.type === 'combo') {
    return (
      <Box component="ul" sx={{ pl: 3, m: 0 }} data-testid="reward-summary-combo">
        {reward.rewards.map((sub, idx) => (
          <Typography
            key={idx}
            component="li"
            variant="body1"
            sx={{ mb: 0.5 }}
            data-testid="reward-summary-line"
          >
            {renderOne(sub)}
          </Typography>
        ))}
      </Box>
    );
  }
  return (
    <Typography
      variant="body1"
      sx={{ fontWeight: 500 }}
      data-testid="reward-summary-line"
    >
      {renderOne(reward)}
    </Typography>
  );
};

export default RewardSummary;
